import React, { useContext } from 'react';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { DestinationContext } from '../../../contexts/DestinationContext';
import { filterActivePhotos } from '../../../utils/photoUtils';
import EyeIcon from '../../../images/icons/eye.svg';
import styles from './FeaturedProperties.module.scss';
import PppnsTooltip from '../../utils/PppnsTooltip/PppnsTooltip';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// `priceOverride` is a free-text admin field (e.g. "$650", "650") —
// reformat the numeric part as USD rather than trusting the source formatting.
const formatUSD = (price = '') => {
  const match = String(price).match(/[\d,]+/);
  const amount = match ? Number(match[0].replace(/,/g, '')) : null;
  return amount != null ? usd.format(amount) : price;
};

const isFlagActive = (flag) => {
  if (flag === false || flag === 'false') return false;
  return true;
};

const FeaturedProperties = ({ showViewAll = true, onOpenPlanner, page }) => {
  const destination = useContext(DestinationContext);
  const navigate = useNavigate();
  let location;
  try {
    location = useLocation();
  } catch (e) {
    location = null;
  }

  const currentPage = page || (location?.pathname?.startsWith('/accommodation') ? 'accommodation' : 'home');

  const featuredProperties = destination?.featuredProperties;
  const isObjectFormat = featuredProperties && !Array.isArray(featuredProperties);
  const rawEntries = isObjectFormat ? featuredProperties.properties : featuredProperties || [];
  const entries = rawEntries.filter((entry) => {
    if (!entry?.property) return false;
    if (currentPage === 'home') {
      return isFlagActive(entry.home);
    }
    if (currentPage === 'accommodation' || currentPage === 'accomodation') {
      return isFlagActive(entry.accommodation ?? entry.accomodation);
    }
    return true;
  });

  if (entries.length === 0) return null;

  const eyebrow = isObjectFormat && featuredProperties.eyebrow ? featuredProperties.eyebrow : "Where You'll Stay";
  const title = isObjectFormat && featuredProperties.title ? featuredProperties.title : `Featured <span class="${styles.accent}">Camps &amp; Lodges</span> in ${destination?.name || ''}`;
  const subtext = isObjectFormat && featuredProperties.subtext ? featuredProperties.subtext : `A hand-picked taste of ${destination?.name || ''}'s finest — from legendary camps to intimate conservancy retreats.`;

  return (
    <section className={styles.section} id="featured-camps">
      <div className={styles.wrap}>
        <div className={styles.header}>
          <div>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h2 className={styles.heading} dangerouslySetInnerHTML={{ __html: title }} />
            <p className={styles.lead}>{subtext}</p>
          </div>
        </div>

        <div className={styles.cards}>
          {entries.map((entry, idx) => {
            const { property } = entry;
            const title = entry.nameOverride || property.title;
            const description =
              entry.descriptionOverride || property.briefDescription;
            const activePhotos = filterActivePhotos(property.photos);
            const image = activePhotos[0];

            return (
              <div className={styles.card} key={entry._id || property._id || idx}>
                <Link
                  to={`/accommodation/${property.slug}`}
                  className={styles.cardLink}
                >
                  <div className={styles.media}>
                    {image?.url && (
                      <img
                        src={image.url}
                        alt={image.title || title}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    {entry.typeOverride && (
                      <span className={styles.badge}>{entry.typeOverride}</span>
                    )}
                    {entry.featured && (
                      <span className={styles.featured}>★ Featured</span>
                    )}
                  </div>
                  <div className={styles.body}>
                    {entry.regionOverride && (
                      <div className={styles.region}>{entry.regionOverride}</div>
                    )}
                    <h3 className={styles.title}>{title}</h3>
                    {description && (
                      <p className={styles.description}>{description}</p>
                    )}
                    {((Array.isArray(entry.tags) && entry.tags.length > 0) || property?.views) && (
                      <div className={styles.tags}>
                        {Array.isArray(entry.tags) && entry.tags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                        <span className={styles.views}>
                          <img src={EyeIcon} alt="views" className={styles.viewIcon} />
                          {property?.views || 0} views
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
                <div className={styles.foot}>
                  {entry.priceOverride && (
                    <div className={styles.price}>
                      <span className={styles.per}>from { ' '}</span>
                      <span className={styles.amt}>
                        {formatUSD(entry.priceOverride)}
                      </span>{' '}
                      <PppnsTooltip className={styles.per} />
                    </div>
                  )}
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.quotesBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onOpenPlanner) {
                          onOpenPlanner({ property, intent: 'quotes' });
                        } else {
                          navigate({
                            to: `/accommodation/${property.slug}`,
                            search: { intent: 'quotes' },
                          });
                        }
                      }}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showViewAll && (
          <div className={styles.footer}>
            <Link to="/accommodation" className={styles.viewAll}>
              View All {destination?.name} Properties →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProperties;
